import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeItem, setActiveItem] = useState('Início');

  // Detecta scroll para mudar estilo do header
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Detecta página atual para marcar item ativo
  useEffect(() => {
    const path = window.location.pathname;
    if (path === '/') setActiveItem('Início');
    else if (path === '/quem-somos') setActiveItem('Quem Somos');
    else if (path === '/contato') setActiveItem('Contato');
    else if (path === '/planos') setActiveItem('Planos');
    else if (path === '/blog') setActiveItem('Blog');
  }, []);

  const navigateTo = (url) => {
    // Verifica se é uma âncora na mesma página
    if (url.startsWith('/#')) {
      const elementId = url.substring(2);
      const element = document.getElementById(elementId);
      
      // Se estiver na home e o elemento existir, faz scroll suave
      if (window.location.pathname === '/' && element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setIsMobileMenuOpen(false);
        return;
      }
    }
    
    // Caso contrário, navega normalmente
    window.location.href = url;
  };

  const menuItems = [
    { label: 'Início', path: '/' },
    { label: 'Serviços MEI', path: '/#servicos-mei' },
    { label: 'Planos', path: '/planos' },
    { label: 'Quem Somos', path: '/quem-somos' },
    { label: 'Contato', path: '/contato' },
  ];

  // Variantes de animação para o menu mobile
  const menuVariants = {
    closed: {
      opacity: 0,
      x: '100%',
      transition: {
        duration: 0.3,
        ease: 'easeInOut'
      }
    },
    open: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.3,
        ease: 'easeInOut'
      }
    }
  };

  const menuItemVariants = {
    closed: { opacity: 0, x: 50 },
    open: (i) => ({
      opacity: 1,
      x: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.3
      }
    })
  };

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled
            ? 'py-2'
            : 'py-3'
        }`}
        style={{
          background: isScrolled
            ? 'rgba(255, 255, 255, 0.95)'
            : 'rgba(255, 255, 255, 0.85)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          boxShadow: isScrolled
            ? '0 4px 30px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(16, 185, 129, 0.1)'
            : '0 2px 20px rgba(0, 0, 0, 0.05)',
          borderBottom: isScrolled
            ? '1px solid rgba(16, 185, 129, 0.2)'
            : '1px solid rgba(255, 255, 255, 0.3)',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 flex justify-between items-center">
          {/* Logo com animação */}
          <motion.div
            onClick={() => navigateTo('/')}
            className="cursor-pointer relative group"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <motion.img
              src="https://res.cloudinary.com/dlk1ta1fr/image/upload/v1741556152/O2-Oxygen-MeuMei_Logo_uaxerh.png"
              alt="MEI Manager Logo"
              className={`transition-all duration-300 ${
                isScrolled ? 'h-14 md:h-16' : 'h-16 md:h-20'
              }`}
            />
            {/* Glow effect no hover */}
            <div
              className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
              style={{
                background: 'radial-gradient(circle at center, rgba(16, 185, 129, 0.2) 0%, transparent 70%)',
                filter: 'blur(10px)',
              }}
            />
          </motion.div>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center">
            <div className="flex items-center bg-gray-50/80 rounded-full p-1.5 mr-6"
              style={{
                boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.06)',
              }}
            >
              {menuItems.map((item, index) => (
                <motion.button
                  key={item.label}
                  onClick={() => {
                    setActiveItem(item.label);
                    navigateTo(item.path);
                  }}
                  className={`relative px-4 py-2.5 text-sm font-medium rounded-full transition-all duration-300 ${
                    activeItem === item.label
                      ? 'text-white'
                      : 'text-gray-600 hover:text-green-600'
                  }`}
                  whileHover={{ scale: activeItem === item.label ? 1 : 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {/* Background animado para item ativo */}
                  {activeItem === item.label && (
                    <motion.div
                      layoutId="activeBackground"
                      className="absolute inset-0 rounded-full"
                      style={{
                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        boxShadow: '0 4px 15px rgba(16, 185, 129, 0.4)',
                      }}
                      transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                    />
                  )}
                  <span className="relative z-10">{item.label}</span>
                </motion.button>
              ))}
            </div>
          </div>

          {/* Desktop Buttons */}
          <div className="hidden md:flex items-center space-x-3">
            <motion.button
              onClick={() => navigateTo('https://app.meumei.net/login')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="relative px-5 py-2.5 text-gray-700 font-medium rounded-full overflow-hidden group"
            >
              {/* Efeito de borda animada */}
              <span className="absolute inset-0 rounded-full border-2 border-transparent group-hover:border-green-500 transition-all duration-300" />
              {/* Efeito de fundo no hover */}
              <span className="absolute inset-0 rounded-full bg-green-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <span className="relative z-10 group-hover:text-green-600 transition-colors duration-300">
                Entre
              </span>
            </motion.button>

            <motion.button
              onClick={() => navigateTo('https://app.meumei.net/register')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="relative px-6 py-2.5 text-white font-semibold rounded-full overflow-hidden group"
              style={{
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                boxShadow: '0 4px 15px rgba(16, 185, 129, 0.4)',
              }}
            >
              {/* Efeito de brilho passando */}
              <motion.span
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
                initial={{ x: '-100%' }}
                whileHover={{ x: '100%' }}
                transition={{ duration: 0.6, ease: 'easeInOut' }}
              />
              {/* Efeito de glow no hover */}
              <span
                className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                style={{
                  boxShadow: '0 0 20px rgba(16, 185, 129, 0.6)',
                }}
              />
              <span className="relative z-10 flex items-center gap-2">
                Cadastre-se
                <motion.svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="group-hover:translate-x-1 transition-transform duration-300"
                >
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </motion.svg>
              </span>
            </motion.button>
          </div>

          {/* Mobile Menu Toggle */}
          <motion.button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden relative w-10 h-10 flex items-center justify-center rounded-full bg-gray-50"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            style={{
              boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
            }}
          >
            <div className="w-5 h-4 flex flex-col justify-between">
              <motion.span
                className="w-full h-0.5 bg-gray-700 rounded-full origin-left"
                animate={isMobileMenuOpen ? { rotate: 45, y: -2 } : { rotate: 0, y: 0 }}
                transition={{ duration: 0.3 }}
              />
              <motion.span
                className="w-full h-0.5 bg-gray-700 rounded-full"
                animate={isMobileMenuOpen ? { opacity: 0, x: -10 } : { opacity: 1, x: 0 }}
                transition={{ duration: 0.3 }}
              />
              <motion.span
                className="w-full h-0.5 bg-gray-700 rounded-full origin-left"
                animate={isMobileMenuOpen ? { rotate: -45, y: 2 } : { rotate: 0, y: 0 }}
                transition={{ duration: 0.3 }}
              />
            </div>
          </motion.button>
        </div>

        {/* Linha de progresso decorativa */}
        <motion.div
          className="absolute bottom-0 left-0 h-0.5"
          style={{
            background: 'linear-gradient(90deg, transparent, #10b981, transparent)',
          }}
          initial={{ width: '0%', opacity: 0 }}
          animate={{
            width: isScrolled ? '100%' : '0%',
            opacity: isScrolled ? 1 : 0,
          }}
          transition={{ duration: 0.5 }}
        />
      </motion.nav>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="fixed inset-0 bg-black/50 z-40 md:hidden"
              style={{ backdropFilter: 'blur(5px)' }}
              onClick={() => setIsMobileMenuOpen(false)}
            />

            {/* Menu Panel */}
            <motion.div
              variants={menuVariants}
              initial="closed"
              animate="open"
              exit="closed"
              className="fixed top-0 right-0 bottom-0 w-[85%] max-w-sm z-50 md:hidden overflow-hidden"
              style={{
                background: 'linear-gradient(180deg, #ffffff 0%, #f0fdf4 100%)',
                boxShadow: '-10px 0 40px rgba(0, 0, 0, 0.15)',
              }}
            >
              {/* Header do menu mobile */}
              <div className="flex items-center justify-between p-6 border-b border-gray-100">
                <img
                  src="https://res.cloudinary.com/dlk1ta1fr/image/upload/v1741556152/O2-Oxygen-MeuMei_Logo_uaxerh.png"
                  alt="MeuMei Logo"
                  className="h-12"
                />
                <motion.button
                  onClick={() => setIsMobileMenuOpen(false)}
                  whileHover={{ scale: 1.1, rotate: 90 }}
                  whileTap={{ scale: 0.9 }}
                  className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </motion.button>
              </div>

              {/* Menu Items */}
              <div className="p-6 space-y-2">
                {menuItems.map((item, index) => (
                  <motion.button
                    key={item.label}
                    custom={index}
                    variants={menuItemVariants}
                    initial="closed"
                    animate="open"
                    onClick={() => {
                      navigateTo(item.path);
                      setIsMobileMenuOpen(false);
                    }}
                    className={`w-full flex items-center justify-between p-4 rounded-2xl text-left transition-all duration-300 group ${
                      activeItem === item.label
                        ? 'bg-green-100 text-green-700'
                        : 'text-gray-700 hover:bg-green-50 hover:text-green-600'
                    }`}
                  >
                    <span className="text-lg font-medium">{item.label}</span>
                    <motion.svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className={`transition-all duration-300 ${
                        activeItem === item.label
                          ? 'opacity-100'
                          : 'opacity-0 group-hover:opacity-100 group-hover:translate-x-1'
                      }`}
                    >
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </motion.svg>
                  </motion.button>
                ))}
              </div>

              {/* Divider decorativo */}
              <div className="mx-6 h-px bg-gradient-to-r from-transparent via-green-200 to-transparent" />

              {/* Botões de ação */}
              <div className="p-6 space-y-3">
                <motion.button
                  custom={menuItems.length}
                  variants={menuItemVariants}
                  initial="closed"
                  animate="open"
                  onClick={() => {
                    navigateTo('https://app.mei.oxygenhub.app/login');
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full p-4 text-center text-gray-700 font-medium rounded-2xl border-2 border-gray-200 hover:border-green-500 hover:text-green-600 transition-all duration-300"
                >
                  Entre na sua conta
                </motion.button>

                <motion.button
                  custom={menuItems.length + 1}
                  variants={menuItemVariants}
                  initial="closed"
                  animate="open"
                  onClick={() => {
                    navigateTo('https://app.mei.oxygenhub.app/register');
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full p-4 text-center text-white font-semibold rounded-2xl transition-all duration-300"
                  style={{
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    boxShadow: '0 4px 20px rgba(16, 185, 129, 0.4)',
                  }}
                >
                  Cadastre-se grátis
                </motion.button>
              </div>

              {/* Footer do menu */}
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <div className="text-center text-sm text-gray-500">
                  <p>Precisa de ajuda?</p>
                  <a
                    href="mailto:contato@oxygengroup.com.br"
                    className="text-green-600 hover:underline"
                  >
                    contato@oxygengroup.com.br
                  </a>
                </div>
              </div>

              {/* Decoração de fundo */}
              <div
                className="absolute -bottom-20 -right-20 w-64 h-64 rounded-full opacity-10 pointer-events-none"
                style={{
                  background: 'radial-gradient(circle, #10b981 0%, transparent 70%)',
                }}
              />
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Spacer para compensar o header fixo */}
      <div className={`transition-all duration-300 ${isScrolled ? 'h-[72px]' : 'h-[88px] md:h-[104px]'}`} />
    </>
  );
};

export default Header;